// "Demander une signature" (SIGN-FE-01): from the document panel, ask members of the office to sign
// the document's current version. Title (the document's name by default), an optional message, one to
// ten signers who can read the document (the API checks and says so), the level and the validity.
// On success the new request opens on its own page.
import { FormField, SkeletonList, UserPicker, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Textarea,
  __css,
  tokens,
} from '@fluentui/react-components';
import { SIGNATURE_LEVELS } from '@batiflow/contracts';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganisationMembers } from '@/features/documents/api';
import { useNotify } from '@/shared/hooks/use-notify';
import { useCreateSignatureRequest, useRefreshSignatures } from './api';
import {
  MAX_SIGNERS,
  MAX_VALIDITY_DAYS,
  emptyDraft,
  errorMessage,
  hasErrors,
  toCreateBody,
  validateDraft,
  type CreateDraft,
  type SignatureLevel,
} from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"text":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"help":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f11d4kpn","Be2twd7":"fy9rknc"},"days":{"D305fb":"f175fatz"}});

export interface RequestSignatureDialogProps {
  orgId: string;
  document: { id: string; title: string };
  onClose: () => void;
}

export function RequestSignatureDialog({ orgId, document, onClose }: RequestSignatureDialogProps) {
  const { t } = useTranslation('signatures');
  const styles = useStyles();
  const notify = useNotify();
  const navigate = useNavigate();
  const members = useOrganisationMembers(orgId);
  const create = useCreateSignatureRequest();
  const refresh = useRefreshSignatures(orgId);
  const [draft, setDraft] = useState<CreateDraft>(() => emptyDraft(document.title));
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const options = (members.data?.items ?? []).map((member) => ({
    id: member.userId,
    label: member.displayName,
  }));
  const errors = validateDraft(draft, t);
  const shown = touched ? errors : {};
  const change = (patch: Partial<CreateDraft>) => {
    setDraft((current) => ({ ...current, ...patch }));
    setFailure(null);
  };

  const submit = async () => {
    setTouched(true);
    if (hasErrors(errors) || create.isPending) return;
    setFailure(null);
    try {
      const request = await create.mutateAsync({ orgId, data: toCreateBody(document.id, draft) });
      await refresh();
      notify({ intent: 'success', message: t('create.done') });
      onClose();
      void navigate({
        to: '/o/$orgId/signatures/$requestId',
        params: { orgId, requestId: request.id },
      });
    } catch (error) {
      setFailure(errorMessage(t, error));
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !create.isPending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('create.title', { title: isolate(document.title) })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.text}>{t('create.intro')}</p>
              <FormField label={t('create.titleLabel')} error={shown.title ?? null} required>
                <Input
                  value={draft.title}
                  maxLength={200}
                  onChange={(_event, data) => {
                    change({ title: data.value });
                  }}
                />
              </FormField>
              <FormField label={t('create.messageLabel')} hint={t('create.messageHint')}>
                <Textarea
                  value={draft.message}
                  maxLength={1000}
                  resize="vertical"
                  onChange={(_event, data) => {
                    change({ message: data.value });
                  }}
                />
              </FormField>
              <FormField
                label={t('create.signersLabel')}
                hint={t('create.signersHint', { max: MAX_SIGNERS })}
                error={shown.signerIds ?? null}
                required
              >
                {members.isPending ? (
                  <SkeletonList count={1} />
                ) : (
                  <UserPicker
                    label={t('create.signersLabel')}
                    options={options}
                    value={draft.signerIds}
                    onChange={(signerIds) => {
                      change({ signerIds });
                    }}
                  />
                )}
              </FormField>
              {members.isError ? (
                <MessageBar intent="warning" layout="multiline">
                  <MessageBarBody>{t('create.membersFailed')}</MessageBarBody>
                </MessageBar>
              ) : null}
              <RadioGroup
                aria-label={t('create.levelLabel')}
                value={draft.level}
                onChange={(_event, data) => {
                  change({ level: data.value as SignatureLevel });
                }}
              >
                {SIGNATURE_LEVELS.map((level) => (
                  <Radio key={level} value={level} label={t(`levels.${level}`)} />
                ))}
              </RadioGroup>
              <p className={styles.help}>{t(`levels.help.${draft.level}`)}</p>
              <FormField
                label={t('create.validityLabel')}
                hint={t('create.validityHint', { max: MAX_VALIDITY_DAYS })}
                error={shown.days ?? null}
                required
              >
                <Input
                  className={styles.days}
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={MAX_VALIDITY_DAYS}
                  value={draft.days}
                  onChange={(_event, data) => {
                    change({ days: data.value });
                  }}
                />
              </FormField>
              {failure ? (
                <MessageBar intent="error" layout="multiline" role="alert">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={create.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={create.isPending}
              onClick={() => {
                void submit();
              }}
            >
              {create.isPending ? t('create.sending') : t('create.send')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/signatures/create-dialog.griffel.css";